/* Tour "entities-consolidation-onboarding" — version onboarding de la
   consolidation. Pour éviter le reload lourd de la création d'entreprise via
   l'UI, on crée une 2ᵈ entreprise de démo EN FOND (actionCreateCompany, sans
   bascule de contexte) et on force la préférence de consolidation côté client.
   On guide ensuite Comptes → Consolider → Valider. À la sortie : suppression de
   l'entreprise de démo + restauration de la préférence. */

import store from '@/store';
import router from '@/router/router';
import bus from '@/bus/app-bus';
import './types';

const DEMO_COMPANY_NAME = 'Okimia';
let savedConsolidationPref: boolean | undefined;

async function onStart() {
  // 1. Force la préférence de consolidation (client-only, reverté à la sortie)
  //    pour que le bouton « Consolider plusieurs entités » apparaisse.
  const prefs = (store as any).state.user?.preferences || {};
  savedConsolidationPref = prefs.consolidation_enabled;
  store.commit('user/setUserPreferences', {
    ...prefs,
    consolidation_enabled: true,
  });
  // 2. Ajoute une 2ᵈ entreprise de démo 100 % CLIENT-ONLY (aucun appel serveur) :
  //    - on la stocke au FORMAT TABLEAU attendu ([nom, …, flagCPA falsy]) pour
  //      qu'elle passe le filtre non-CPA et s'affiche dans la consolidation ;
  //    - son id est mémorisé : actionGetBanksForMC renvoie [] pour cet id (garde
  //      dans bank.js) → pas de fetch serveur qui échouerait.
  //    Rien n'est créé en base ; retirée du state à la sortie.
  let id = '';
  try {
    id = (window as any).utils.genId();
  } catch {
    id = `tour-company-${Date.now()}`;
  }
  try {
    localStorage.setItem('fygr-tour-company', id);
  } catch {
    /* ignore */
  }
  store.commit('user/addCompany', [id, [DEMO_COMPANY_NAME, null, null, false]]);
}

async function onExit() {
  // Retire l'entreprise de démo du state (client-only → aucun appel serveur).
  let id: string | null = null;
  try {
    id = localStorage.getItem('fygr-tour-company');
  } catch {
    id = null;
  }
  if (id) {
    store.commit('user/removeFromCompanies', id);
    try {
      localStorage.removeItem('fygr-tour-company');
    } catch {
      /* ignore */
    }
  }
  // Restaure la préférence de consolidation.
  const prefs = (store as any).state.user?.preferences || {};
  store.commit('user/setUserPreferences', {
    ...prefs,
    consolidation_enabled: savedConsolidationPref,
  });
}

window.FygrTour.register('entities-consolidation-onboarding', {
  title: 'Consolider vos entités',
  page: '/dashboard',
  interactive: false,
  // Ce tour cible le switcher d'entreprise (dans la sidebar) → on garde la
  // sidebar visible (poussée à droite du rail du tuto).
  keepSidebar: true,
  intro: {
    title: 'Consolider plusieurs entités',
    body: 'La consolidation réunit plusieurs sociétés dans une vue de trésorerie unique. Nous avons ajouté une entreprise d’exemple pour la démonstration.',
    note: 'L’entreprise d’exemple est supprimée automatiquement à la fin.',
    startLabel: 'Commencer',
    optoutLabel: 'Désactiver les guides automatiques',
    optoutNote: 'Ils ne s’afficheront plus automatiquement. Vous pourrez les relancer à tout moment depuis « Tutoriels ».',
  },
  onStart,
  onExit,
  steps: [
    {
      // Ouvre le sélecteur d'entreprise (en haut de la barre latérale).
      target: '[data-tour-step="company-switcher"]',
      title: 'Vos entreprises',
      body: 'Cliquez ici pour ouvrir la liste de vos entreprises.',
      placement: 'right',
      clickToAdvance: true,
      waitFor: '[data-tour="company-dd"] .wrapper',
      waitTimeout: 5000,
    },
    {
      // Explication du dropdown : on ne crée rien via ce tuto — l'entreprise
      // fictive « Okimia » a déjà été ajoutée (client-only) pour illustrer.
      // La boîte visible est `.wrapper` ; `.f-dropdown-overlay` est un conteneur
      // plein écran (position:fixed, 100%×100%). Plusieurs dropdowns de l'app
      // partagent cette classe → on cible via l'attribut unique `data-tour` pour
      // que le spotlight n'entoure QUE le dropdown entreprise.
      target: '[data-tour="company-dd"] .wrapper',
      title: 'Vos entités',
      body: `Ici, vous retrouvez vos entités et pouvez en ajouter une. Pour illustrer la consolidation, nous avons ajouté une entreprise fictive « ${DEMO_COMPANY_NAME} ».`,
      placement: 'right',
      // lockTarget + strictTarget : étape purement explicative. strictTarget
      // neutralise le whitelist `.f-dropdown-overlay` → plus rien n'est cliquable
      // dans le dropdown, notamment « Ajouter une entreprise ».
      lockTarget: true,
      strictTarget: true,
      waitTimeout: 5000,
      onNext: async () => {
        // Ferme le dropdown entreprise via l'event bus (PAS d'Escape : le moteur
        // du tuto écoute Escape et quitterait le tour → suppression d'Okimia).
        bus.$emit('tour:close-company-dropdown');
        await new Promise((r) => setTimeout(r, 150));
        await router.push('/dashboard').catch(() => {});
      },
    },
    // ── Consolidation ────────────────────────────────────────────────────────
    {
      target: '[data-tour-step="comptes-pill"]',
      title: 'Ouvrez le menu Comptes',
      body: 'Cliquez sur « Comptes » pour ouvrir la liste de vos entités et accéder à la consolidation.',
      placement: 'bottom-start',
      clickToAdvance: true,
      waitFor: '[data-tour-step="consolider-trigger"]',
      waitTimeout: 5000,
      // « Suivant » comme le clic sur « Comptes » doivent ouvrir le menu : la zone
      // cliquable est le .c-dropdown interne (l'ancre est sur la racine).
      onNext: () => {
        if (!document.querySelector('[data-tour-step="consolider-trigger"]')) {
          (
            document.querySelector(
              '[data-tour-step="comptes-pill"] .c-dropdown',
            ) as HTMLElement | null
          )?.click();
        }
      },
    },
    {
      target: '[data-tour-step="consolider-trigger"]',
      title: 'Activez la consolidation',
      body: 'Cliquez sur « Consolider plusieurs entités » pour choisir les entités à réunir.',
      placement: 'right-start',
      clickToAdvance: true,
      // Le panneau est un dropdown (.f-dropdown-overlay, whitelisté par défaut) :
      // strictTarget neutralise ce whitelist → seule la cible reste cliquable.
      strictTarget: true,
      waitFor: '[data-tour-step="consolidate-company-check"]',
      waitTimeout: 5000,
    },
    {
      // Coche l'entreprise de démo (Okimia), décochée par défaut pendant le guide.
      // La Checkbox bascule sur mousedown (@mousedown.stop) → on N'utilise PAS
      // clickToAdvance (le click n'avance pas de façon fiable) mais advanceWhen :
      // l'étape passe dès que la case d'Okimia est cochée (icône fi-checkbox-on).
      target: '[data-tour-step="consolidate-company-check"]',
      title: 'Sélectionnez et validez vos entités',
      body: `Cochez « ${DEMO_COMPANY_NAME} » pour l’inclure dans la consolidation.`,
      placement: 'right',
      strictTarget: true,
      advanceWhen: '[data-tour-step="consolidate-company-check"] .fi-checkbox-on',
      waitTimeout: 5000,
    },
    {
      target: '[data-tour-step="valider-btn"]',
      title: 'Sélectionnez et validez vos entités',
      body: 'Cliquez sur « Valider » pour appliquer et voir vos entités fusionnées.',
      placement: 'right',
      clickToAdvance: true,
      // Même numéro d'étape que le cochage : une seule étape logique, deux bulles.
      groupWithPrevious: true,
      // Verrouille la checklist des entités (dropdown whitelisté) : seul
      // « Valider » reste cliquable pendant cette étape.
      strictTarget: true,
      // Laisse la consolidation s'appliquer (fermeture du dropdown + updateData)
      // avant d'afficher le récap sur le pill « X entités consolidées ».
      nextDelay: 600,
    },
    {
      // Récap final : le pill « Comptes » affiche désormais « X entités
      // consolidées » (même élément que comptes-pill).
      target: '[data-tour-step="comptes-pill"]',
      title: 'Vos entités sont réunies',
      body: 'Vos données sont maintenant fusionnées : votre tableau de trésorerie agrège toutes les entités sélectionnées. Vous pouvez revenir à une seule entité à tout moment depuis ce menu.',
      placement: 'bottom-start',
      lockTarget: true,
      waitTimeout: 5000,
    },
  ],
  celebration: {
    title: 'Vos entités sont consolidées !',
    body: 'Vous voyez maintenant vos entités réunies dans une vue de trésorerie unique. L’entreprise d’exemple a été supprimée.',
    drawer: true,
  },
});

export {};
